/**
 * Tabela "Por empresa" / "Por grupo" do relatório de Composição de Saldo.
 *
 * Pedido do usuário (08/10/2026): "o saldo que conseguimos compor e o saldo em
 * aberto do que conseguimos compor, além do saldo total etc., isso para cada
 * empresa (ou também grupo de empresas, matrizes e filiais)".
 *
 * A coluna que muda a leitura é `% casado`: ela EXCLUI do denominador o que não
 * tem par por natureza (compensação de cartão, transferência do próprio grupo).
 * Sem isso o percentual fica deprimido por valor que nunca poderia casar —
 * medido na empresa 856: 57,1% contra 71,0%.
 *
 * Toda regra de leitura vive em `relatorio.ts` (função pura, testada). Aqui só
 * renderiza.
 */

import React from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { CaretDown, CaretRight, Info } from 'phosphor-react';

import { IRelatorioEmpresa, IRelatorioGrupo } from '../../../auditoriaInteligente/types';
import {
  AJUDA_BASE_DESPROPORCIONAL,
  corDoPct,
  fmtInt,
  fmtPct,
  fmtValor,
  loteAntesDosRotulos,
  rotuloGrupo,
  temSemParNatureza,
  textoLigacoes,
  visiveisTemDesproporcional,
} from './relatorio';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  linha: 'rgba(15, 23, 42, 0.06)',
  warn: '#D97706',
  warnBg: 'rgba(217, 119, 6, 0.08)',
};

type Modo = 'empresa' | 'grupo';

interface IProps {
  modo: Modo;
  empresas: IRelatorioEmpresa[];
  grupos: IRelatorioGrupo[];
  /** Quantas linhas mostrar antes do "ver todas". */
  limite?: number;
}

const Celula: React.FC<{
  children: React.ReactNode;
  alinha?: 'left' | 'right';
  largura?: string;
  cor?: string;
  peso?: string;
  titulo?: string;
}> = ({ children, alinha = 'right', largura, cor, peso, titulo }) => (
  <Box
    flex={largura ? undefined : 1}
    w={largura}
    minW={largura}
    textAlign={alinha}
    fontSize="11.5px"
    fontWeight={peso ?? '500'}
    color={cor ?? COR.ink}
    sx={{ fontVariantNumeric: 'tabular-nums' }}
    whiteSpace="nowrap"
    overflow="hidden"
    textOverflow="ellipsis"
    title={titulo}
  >
    {children}
  </Box>
);

const Cabecalho: React.FC<{
  texto: string;
  alinha?: 'left' | 'right';
  largura?: string;
  ajuda?: string;
}> = ({ texto, alinha = 'right', largura, ajuda }) => (
  <Box
    flex={largura ? undefined : 1}
    w={largura}
    minW={largura}
    textAlign={alinha}
    fontSize="10px"
    fontWeight="700"
    letterSpacing="0.04em"
    textTransform="uppercase"
    color={COR.faint}
    whiteSpace="nowrap"
  >
    {ajuda ? (
      <Tooltip label={ajuda} fontSize="11px" placement="top" hasArrow>
        <Flex
          align="center"
          justify={alinha === 'right' ? 'flex-end' : 'flex-start'}
          gap={1}
          cursor="help"
        >
          {texto}
          <Info size={11} />
        </Flex>
      </Tooltip>
    ) : (
      texto
    )}
  </Box>
);

export const TabelaPorEmpresa: React.FC<IProps> = ({ modo, empresas, grupos, limite = 12 }) => {
  const [abertos, setAbertos] = React.useState<Record<string, boolean>>({});
  const [verTodas, setVerTodas] = React.useState(false);

  const linhas: (IRelatorioEmpresa | IRelatorioGrupo)[] = modo === 'grupo' ? grupos : empresas;
  // O aviso usa SEMPRE a lista por empresa: é nela que `orfas` existe em todas
  // as linhas, e o estado do lote é o mesmo nos dois modos.
  const mostraNatureza = temSemParNatureza(empresas);
  const avisoLote = loteAntesDosRotulos(empresas);
  // a legenda do asterisco so' aparece quando ha' asterisco na tabela
  const temDesproporcional = visiveisTemDesproporcional(linhas, verTodas, limite);
  const visiveis = verTodas ? linhas : linhas.slice(0, limite);

  if (!linhas.length) {
    return (
      <Text fontSize="12px" color={COR.muted}>
        Nenhuma empresa com composição calculada neste recorte.
      </Text>
    );
  }

  return (
    <Box>
      {avisoLote && (
        <Flex align="flex-start" gap={2} bg={COR.warnBg} borderRadius="8px" px={3} py={2} mb={3}>
          <Box color={COR.warn} mt="1px">
            <Info size={13} />
          </Box>
          <Text fontSize="11px" color={COR.warn} lineHeight="1.5">
            Este lote foi calculado antes de a classificação por natureza existir, então a coluna{' '}
            <b>não tem par</b> ainda aparece zerada e o <b>% casado</b> está deprimido por valor que
            nunca poderia casar. O próximo processamento corrige.
          </Text>
        </Flex>
      )}

      {/* cabeçalho */}
      <Flex gap={3} px={2} pb={2} borderBottom="1.5px solid" borderColor={COR.linha} align="center">
        <Box w="18px" minW="18px" />
        <Cabecalho texto={modo === 'grupo' ? 'Grupo' : 'Empresa'} alinha="left" largura="230px" />
        <Cabecalho texto="Contas" largura="62px" />
        <Cabecalho
          texto="Saldo total"
          largura="118px"
          ajuda="Saldo das contas que compõem, somado em módulo."
        />
        <Cabecalho
          texto="Provisionado"
          largura="118px"
          ajuda="O título gerado no período: venda a prazo, nota de entrada."
        />
        <Cabecalho texto="Em aberto" largura="118px" ajuda="O que ainda falta receber ou pagar." />
        <Cabecalho
          texto="Já pago"
          largura="112px"
          ajuda="Quanto do título já foi pago E ligado a um pagamento identificado."
        />
        <Cabecalho
          texto="Sem par"
          largura="112px"
          ajuda="Baixa que não achou título. Inclui o que não tem par por natureza."
        />
        {mostraNatureza && (
          <Cabecalho
            texto="Não tem par"
            largura="112px"
            ajuda="Compensação de cartão e transferência do próprio grupo: não existe título individual para casar. Não é falha de conciliação."
          />
        )}
        <Cabecalho
          texto="% casado"
          largura="74px"
          ajuda="Provisionado ÷ (provisionado + sem par − o que não tem par). Mostra “—” quando não há base para medir."
        />
      </Flex>

      {visiveis.map(l => {
        const ehGrupo = modo === 'grupo';
        const g = l as IRelatorioGrupo;
        const e = l as IRelatorioEmpresa;
        const chave = ehGrupo
          ? (g.raiz_cnpj ?? `g-${g.empresas?.[0]?.external_id ?? '?'}`)
          : String(e.external_id);
        const expansivel = ehGrupo && g.multi_estabelecimento;
        const aberto = !!abertos[chave];
        const nome = ehGrupo ? rotuloGrupo(g) : (e.empresa ?? `Empresa ${e.external_id}`);

        return (
          <Box key={chave}>
            <Flex
              gap={3}
              px={2}
              py={2.5}
              align="center"
              borderBottom="1px solid"
              borderColor={COR.linha}
              _hover={{ bg: 'rgba(27, 136, 245, 0.03)' }}
              cursor={expansivel ? 'pointer' : 'default'}
              onClick={expansivel ? () => setAbertos(a => ({ ...a, [chave]: !aberto })) : undefined}
            >
              <Box w="18px" minW="18px" color={COR.faint}>
                {expansivel && (aberto ? <CaretDown size={12} /> : <CaretRight size={12} />)}
              </Box>
              <Celula alinha="left" largura="230px" peso="600" titulo={nome}>
                {nome}
              </Celula>
              <Celula largura="62px" cor={COR.muted}>
                {fmtInt(l.contas)}
              </Celula>
              <Celula largura="118px">{fmtValor(l.saldo_razao)}</Celula>
              <Celula largura="118px">{fmtValor(l.provisionado)}</Celula>
              <Celula largura="118px">{fmtValor(l.em_aberto)}</Celula>
              <Celula
                largura="112px"
                cor={Number(l.ligado) > 0 ? '#059669' : COR.faint}
                titulo={`${textoLigacoes(l)} títulos com pagamento ligado`}
              >
                {fmtValor(l.ligado)}
              </Celula>
              <Celula largura="112px" cor={COR.muted}>
                {fmtValor(l.sem_par)}
              </Celula>
              {mostraNatureza && (
                <Celula largura="112px" cor={COR.faint}>
                  {fmtValor(l.sem_par_natureza)}
                </Celula>
              )}
              <Celula
                largura="74px"
                cor={corDoPct(l.pct_casado, l.base_desproporcional)}
                peso="700"
                titulo={l.base_desproporcional ? AJUDA_BASE_DESPROPORCIONAL : undefined}
              >
                {fmtPct(l.pct_casado)}
                {l.base_desproporcional && (
                  <Text as="span" ml={1} color={COR.faint} fontWeight="400">
                    *
                  </Text>
                )}
              </Celula>
            </Flex>

            {/* os estabelecimentos do grupo */}
            {expansivel && aberto && (
              <Box pl="34px" bg="rgba(15, 23, 42, 0.015)">
                {(g.empresas ?? []).map(emp => (
                  <Flex
                    key={emp.external_id}
                    gap={3}
                    px={2}
                    py={1.5}
                    borderBottom="1px solid"
                    borderColor={COR.linha}
                  >
                    <Celula alinha="left" largura="214px" cor={COR.muted}>
                      {emp.empresa ?? `Empresa ${emp.external_id}`}
                    </Celula>
                    <Celula alinha="left" largura="150px" cor={COR.faint}>
                      {emp.cnpj ?? '—'}
                    </Celula>
                    <Celula alinha="left" cor={COR.faint}>
                      cód. {emp.external_id}
                    </Celula>
                  </Flex>
                ))}
                <Text fontSize="10.5px" color={COR.warn} px={2} py={2} lineHeight="1.5">
                  Soma <b>simples</b> dos estabelecimentos, não consolidação: transações entre eles
                  não são eliminadas (ITG 2000 R1, item 24).
                </Text>
              </Box>
            )}
          </Box>
        );
      })}

      {temDesproporcional && (
        <Text fontSize="10.5px" color={COR.muted} pt={3} lineHeight="1.6">
          <b>*</b> {AJUDA_BASE_DESPROPORCIONAL}
        </Text>
      )}

      {linhas.length > limite && (
        <Flex justify="center" pt={3}>
          <Text
            fontSize="11px"
            fontWeight="600"
            color="#1B88F5"
            cursor="pointer"
            onClick={() => setVerTodas(v => !v)}
          >
            {verTodas
              ? 'Mostrar menos'
              : `Ver todas (${fmtInt(linhas.length)} ${modo === 'grupo' ? 'grupos' : 'empresas'})`}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default TabelaPorEmpresa;
